"use client";

import { cn } from "@/utils/utils";
import { Loader2 } from "lucide-react";
import type * as React from "react";

export interface DashboardCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  subtitle?: string;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
  };
  isActive?: boolean;
  index?: number;
  waveColor?: "blue" | "green" | "yellow" | "red" | "purple" | "indigo";
  iconColor?: "blue" | "green" | "yellow" | "red" | "purple" | "indigo";
  className?: string;
  onClick?: () => void;
  loading?: boolean;
}

export function DashboardCard({
  title,
  value,
  icon,
  subtitle,
  trend,
  waveColor = "blue",
  iconColor = "blue",
  isActive = false,
  className,
  onClick,
  index,
  loading,
  ...props
}: DashboardCardProps) {
  const left =
    index === 0
      ? "left-[-15px]"
      : index === 1
      ? "left-[-30px]"
      : "left-[-50px]";

  // const left =
  //   id === "school"
  //     ? "left-[-15px]"
  //     : id === "students"
  //     ? "left-[-30px]"
  //     : "left-[-50px]";
  return (
    <div
      className={`p-4 h-[105px] cursor-pointer w-full relative rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 hover:shadow-md flex items-center justify-between ${
        isActive ? "bg-[#FFF5F9]" : "bg-white"
      }`}
      onClick={onClick}
      {...props}
    >
      {/* Wave Background */}
      <div className="">
        <img
          className={`absolute bottom-0 ${left} w-full h-full`}
          src="/images/dashboard.png"
          alt=""
        />
      </div>

      {/* Icon */}

      <div className="flex flex-row w-full justify-between items-center">
        {/* Content */}
        <div className="relative z-5 h-full flex flex-col space-y-2 pl-2">
          {/* Title */}
          <h3
            className="text-md font-semibold text-[#1F1F1F] leading-none"
            style={{
              lineHeight: "22px",
            }}
          >
            {title}
          </h3>

          {/* Value */}

          {loading ? (
            <span className="h-[48px] w-[48px] flex items-center justify-start">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
            </span>
          ) : (
            <span className="text-[32px] font-bold text-[#1F1F1F]">
              {typeof value === "number" ? value.toLocaleString() : value}
            </span>
          )}

          {/* Subtitle and Trend */}
          {(subtitle || trend) && (
            <div className="mt-auto pt-2">
              {subtitle && (
                <p className="text-xs text-gray-500 mb-1">{subtitle}</p>
              )}
              {trend && (
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      trend.isPositive ? "text-green-600" : "text-red-600"
                    )}
                  >
                    {trend.isPositive ? "+" : ""}
                    {trend.value}%
                  </span>
                  {trend.label && (
                    <span className="text-xs text-gray-500">{trend.label}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {icon && (
          <div
            className={`flex items-center justify-center w-[50px] h-[50px] ${
              isActive ? "bg-[#ffffff]" : "bg-[#F2F2F2]"
            } rounded-xl`}
          >
            <div className="text-gray-700">{icon}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Preset card variants for common use cases
export const SchoolsCard = (
  props: Omit<DashboardCardProps, "waveColor" | "iconColor">
) => <DashboardCard waveColor="blue" iconColor="blue" {...props} />;

export const StudentsCard = (
  props: Omit<DashboardCardProps, "waveColor" | "iconColor">
) => <DashboardCard waveColor="green" iconColor="green" {...props} />;

export const CoursesCard = (
  props: Omit<DashboardCardProps, "waveColor" | "iconColor">
) => <DashboardCard waveColor="purple" iconColor="purple" {...props} />;

export const RevenueCard = (
  props: Omit<DashboardCardProps, "waveColor" | "iconColor">
) => <DashboardCard waveColor="yellow" iconColor="yellow" {...props} />;
